import CloseIcon from '@ordr-as/icons/CloseIcon';
import type { ExpoDisplayItem } from '../types';
import { formatOrderedAt } from '../utils';
import StationChip from '../board/StationChip';

type Props = {
  bookingOrderId: number;
  createdUtc?: string;
  orderComment?: string | null;
  displayItems: ExpoDisplayItem[];
  stationNameById: Map<number, string>;
  onClose: () => void;
};

const OrderDetailSheet = ({ bookingOrderId, createdUtc, orderComment, displayItems, stationNameById, onClose }: Props) => {
  const orderedAt = formatOrderedAt(createdUtc);

  return (
    <>
      <div className="flex items-start justify-between gap-2 pl-4 pr-2 pt-2 flex-none">
        <div className="text-xl font-bold text-brand-secondary">#{bookingOrderId}</div>
        <button
          className="flex-none w-6 h-6 rounded-full flex items-center justify-center text-brand-secondary hover:bg-grey-lightest"
          onClick={onClose}
          title="Close"
        >
          <CloseIcon width={18} height={18} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-5 flex flex-col gap-4">
        {orderedAt && <div className="text-extra-small text-grey-dark">Ordered: {orderedAt}</div>}

        <div className="flex flex-col gap-2">
          <h3 className="m-0 text-extra-small font-semibold uppercase tracking-wide text-grey-dark">Order</h3>
          <div className="rounded bg-brand-white px-4 py-3 flex flex-col gap-2">
            {displayItems.map((item, index) => (
              <div
                key={item.key}
                className={`py-1 flex flex-col items-start gap-1 ${
                  index > 0 ? 'border-t border-grey-lightest pt-2' : ''
                }`}
              >
                <span className="text-small font-semibold text-brand-black">
                  {item.quantity} × {item.name}
                </span>
                {item.modifiers.map((modifier, modifierIndex) => (
                  <span key={modifierIndex} className="text-extra-small text-grey-dark">
                    → {modifier}
                  </span>
                ))}
                {item.comment && <span className="text-extra-small italic text-grey-dark">"{item.comment}"</span>}
                <div className="flex gap-1 flex-wrap">
                  {item.chips.map((chip, chipIndex) => (
                    <StationChip
                      key={chipIndex}
                      label={stationNameById.get(chip.stationId) ?? 'Unknown station'}
                      state={chip.state}
                    />
                  ))}
                </div>
              </div>
            ))}
            {displayItems.length === 0 && <div className="text-small text-grey-dark py-1">No order lines.</div>}
          </div>
        </div>

        {orderComment && (
          <div className="flex flex-col gap-2">
            <h3 className="m-0 text-extra-small font-semibold uppercase tracking-wide text-grey-dark">Comment</h3>
            <div className="rounded bg-brand-white px-4 py-3 text-small italic text-grey-dark">
              "{orderComment}"
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default OrderDetailSheet;
